import React, { useState } from 'react';
import { Button } from '@mui/material';
import Loading from 'components/Messages/Loading/Loading';
import Error from 'components/Messages/Error/Error';
import {
  ActivitiesContextProvider,
  ActivityInterface,
  useActivitiesContext,
} from 'pages/Activities/activities.context';
import ActivitiesTable from 'pages/Activities/Table/Table';
import { StoresContextProvider } from 'pages/Stores/stores.context';
import { UsersContextProvider } from 'context/users.context';
import SectionHeader from 'components/SectionHeader/SectionHeader';
import { Link } from 'react-router-dom';
import ContentWrapper from 'components/StyledComponents/ContentWrapper';
import PermissionCheck from 'components/Abilities/PermissionCheck';
import { hasPermissions } from 'config/ability';
import { Div } from 'components/Elements/Div/Div';
import DropdownMenu from 'components/DropdownMenu/DropdownMenu';
import Kebab from 'components/Buttons/Kebab';
import { useAuthContext } from 'context/auth.context';

const ActivitiesComponent = (): JSX.Element => {
  const { loading, error, duplicateActivities } = useActivitiesContext();
  const [checkedRows, setCheckedRows] = useState<ActivityInterface[]>([]);
  const { ability } = useAuthContext();

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error message='There was an error loading Activities' />;
  }

  const handleDuplicateActivities = async () => {
    await duplicateActivities(
      checkedRows.map((checkedRow) => checkedRow.id),
      () => setCheckedRows([])
    );
  };

  const dropdownItems = [
    {
      label: 'Duplicate',
      onClick: () => handleDuplicateActivities(),
      disabled: !checkedRows.length,
      hide: !hasPermissions(ability, 'create', 'Activity'),
    },
  ];

  const headerActions = (
    <Div display='flex'>
      <PermissionCheck permission={{ action: 'create', subject: 'Activity' }}>
        <Button component={Link} variant='contained' color='secondary' to={'/activities/create'}>
          Create Activity
        </Button>
      </PermissionCheck>
      {/* Separating these permission check blocks now in case different options with different permissions levels are added to the Kebab */}
      <PermissionCheck permission={{ action: 'create', subject: 'Activity' }}>
        <Div display='flex' margin='0 0 0 0.75rem'>
          <DropdownMenu items={dropdownItems} button={<Kebab />} />
        </Div>
      </PermissionCheck>
    </Div>
  );

  return (
    <>
      <SectionHeader title='Activities' actions={headerActions} pageHeader />
      <ContentWrapper>
        <ActivitiesTable checkedRows={checkedRows} setCheckedRows={setCheckedRows} />
      </ContentWrapper>
    </>
  );
};

// wrapping component in context provider for full access
const Activities = () => (
  <UsersContextProvider>
    <ActivitiesContextProvider>
      <StoresContextProvider>
        <ActivitiesComponent />
      </StoresContextProvider>
    </ActivitiesContextProvider>
  </UsersContextProvider>
);

export default Activities;
